import { Component } from '@theme/component';
import { ThemeEvents, BackInStockSubscribedEvent, NewsletterSubscribedEvent, eventBus } from '@theme/events';
import { subscribeToNewsletter } from '@theme/newsletter--api';

const BACK_IN_STOCK_API = 'https://app.backinstock.org/stock_notification/create.json';

const { backInStockRequested } = ThemeEvents;

/**
 * @typedef {Object} BackInStockModalRefs
 * @property {HTMLDialogElement} dialog - The native dialog element
 * @property {HTMLImageElement} [image] - Product image element
 * @property {HTMLElement} [title] - Product title element
 * @property {HTMLElement} [variant] - Variant label element
 * @property {HTMLElement} [color] - Color label element
 * @property {HTMLFormElement} form - The notification form
 * @property {HTMLInputElement} email - Email input
 * @property {HTMLInputElement} variantId - Hidden variant ID input
 * @property {HTMLElement} [message] - Success/error message container
 */

/**
 * Back In Stock Modal Component
 *
 * A singleton modal that displays a notification signup form when users click
 * on unavailable variants. Listens for BackInStockRequestEvent on document.
 *
 * The modal content is composable via Liquid - this JS only handles:
 * - Opening/closing the dialog
 * - Populating product data from the event
 * - Form submission (placeholder for backend integration)
 * - Focus management
 *
 * @extends {Component<BackInStockModalRefs>}
 *
 * @example HTML structure (from Liquid snippet)
 * <back-in-stock-modal>
 *   <dialog ref="dialog">
 *     <img ref="image" />
 *     <span ref="title"></span>
 *     <span ref="variant"></span>
 *     <form ref="form">
 *       <input ref="email" type="email" required />
 *       <input ref="variantId" type="hidden" name="variant_id" />
 *       <button type="submit">Notify Me</button>
 *     </form>
 *     <div ref="message" hidden></div>
 *   </dialog>
 * </back-in-stock-modal>
 */
class BackInStockModal extends Component {
	/** @type {(() => void)|null} */
	#unsubscribe = null;

	/** @type {HTMLElement|null} */
	#triggerElement = null;

	/** @type {boolean} */
	#isSubmitting = false;

	connectedCallback() {
		super.connectedCallback();

		this.#unsubscribe = eventBus.on(
			backInStockRequested,
			/** @param {CustomEvent} event */
			(event) => this.#handleRequest(event)
		);

		this.refs.dialog?.addEventListener('close', () => this.#handleClose());
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#unsubscribe?.();
		this.#unsubscribe = null;
	}

	/**
	 * Handle incoming back-in-stock request event
	 * @param {CustomEvent} event
	 */
	#handleRequest(event) {
		const { data } = event.detail ?? {};
		if (!data) return;

		const { productTitle, productUrl, productImage, productId, variantId, variantTitle, productColor, triggerElement } = data;

		this.#triggerElement = triggerElement ?? null;

		if (this.refs.image) {
			this.refs.image.src = productImage ?? '';
			this.refs.image.alt = productTitle ?? '';
		}

		if (this.refs.title) {
			this.refs.title.textContent = productTitle ?? '';
			if (productUrl && this.refs.title.tagName === 'A') {
				/** @type {HTMLAnchorElement} */ (this.refs.title).href = productUrl;
			}
		}

		if (this.refs.variant) {
			const sizeLabel = this.dataset.sizeLabel;
			this.refs.variant.textContent = variantTitle ? (sizeLabel ? `${sizeLabel}: ${variantTitle}` : variantTitle) : '';
		}

		if (this.refs.color) {
			const colorLabel = this.dataset.colorLabel;
			this.refs.color.textContent = productColor ? (colorLabel ? `${colorLabel}: ${productColor}` : productColor) : '';
			this.refs.color.hidden = !productColor;
		}

		if (this.refs.variantId) {
			this.refs.variantId.value = String(variantId ?? '');
		}

		if (this.refs.productId) {
			this.refs.productId.value = String(productId ?? '');
		}

		if (this.refs.message) {
			this.refs.message.hidden = true;
		}

		if (this.refs.error) {
			this.refs.error.hidden = true;
		}

		this.refs.form?.reset();
		this.open();
	}

	/**
	 * Open the modal dialog
	 */
	open() {
		this.refs.dialog?.showModal();
		// The email input carries `autofocus`, which the dialog focusing steps honour
		// over the first focusable (the close button). Chrome finishes those steps after
		// a rAF, so the fallback focus waits a macrotask to avoid being overridden.
		setTimeout(() => this.refs.email?.focus(), 0);
	}

	/**
	 * Close the modal dialog
	 */
	close() {
		this.refs.dialog?.close();
	}

	/**
	 * Handle dialog close (return focus to trigger)
	 */
	#handleClose() {
		if (this.#triggerElement && typeof this.#triggerElement.focus === 'function') {
			this.#triggerElement.focus();
		}
		this.#triggerElement = null;
	}

	/**
	 * Handle form submission — creates the notification via the Back in Stock
	 * app (app.backinstock.org) and optionally subscribes the email to the
	 * Klaviyo newsletter list when the checkbox is ticked.
	 * @param {SubmitEvent} event
	 */
	async handleSubmit(event) {
		event.preventDefault();

		const form = this.refs.form;
		if (!form || !form.checkValidity()) {
			form?.reportValidity();
			return;
		}

		if (this.#isSubmitting) return;

		const formData = new FormData(form);
		const email = String(formData.get('email') ?? '').trim();
		const variantId = String(formData.get('variant_id') ?? '');
		const productId = String(formData.get('product_id') ?? '');
		const wantsNewsletter = formData.get('newsletter') != null;
		const shopDomain = this.dataset.shopDomain;

		if (!email || !shopDomain) {
			this.#showError();
			return;
		}

		this.#isSubmitting = true;
		this.dataset.state = 'loading';
		if (this.refs.error) this.refs.error.hidden = true;

		const params = new URLSearchParams({
			shop: shopDomain,
			'notification[email]': email,
			'notification[quantity_required]': '1',
			'notification[accepts_marketing]': 'true',
		});
		if (productId) params.set('notification[product_no]', productId);
		if (variantId) params.set('variant[variant_no]', variantId);

		try {
			const response = await fetch(`${BACK_IN_STOCK_API}?${params.toString()}`);
			const data = await response.json();

			if (String(data.status ?? '').toUpperCase() !== 'OK') {
				const detail = data.errors ? Object.values(data.errors).flat().join(', ') : '';
				this.#showError(detail);
				return;
			}

			this.#showMessage('success');
			eventBus.emit(new BackInStockSubscribedEvent({ data: { variantId, productId } }));

			const { klaviyoCompanyId, klaviyoListId } = this.dataset;
			if (wantsNewsletter && klaviyoCompanyId && klaviyoListId) {
				subscribeToNewsletter({
					companyId: klaviyoCompanyId,
					listId: klaviyoListId,
					email,
					source: 'Back in Stock',
				})
					.then(() => eventBus.emit(new NewsletterSubscribedEvent({ data: { source: 'Back in Stock' } })))
					.catch((error) => console.error('[back-in-stock] newsletter subscribe failed', error));
			}
		} catch (error) {
			console.error('[back-in-stock]', error);
			this.#showError();
		} finally {
			this.#isSubmitting = false;
			this.dataset.state = '';
		}
	}

	/**
	 * Show the error line — with the API's detail when available, otherwise
	 * the server-rendered generic message.
	 * @param {string} [detail]
	 */
	#showError(detail) {
		const error = this.refs.error;
		if (!error) return;
		if (detail) error.textContent = detail;
		error.hidden = false;
	}

	/**
	 * Show success or error message
	 * @param {'success' | 'error'} type
	 */
	#showMessage(type) {
		const message = this.refs.message;
		if (!message) return;

		message.hidden = false;
		message.dataset.type = type;

		if (type === 'success') {
			this.refs.form?.setAttribute('hidden', '');
		}
	}

	/**
	 * Handle click on backdrop to close
	 * @param {MouseEvent} event
	 */
	handleDialogClick(event) {
		// Component's delegation proxies `event.target` to the element carrying the
		// `on:click` attribute (the dialog itself), so compare against the real
		// click origin: close only when it lands outside the content container.
		const origin = /** @type {Element | undefined} */ (event.composedPath()[0]);
		if (origin instanceof Element && origin.closest('.bis-modal__container')) return;
		this.close();
	}
}

if (!customElements.get('back-in-stock-modal')) {
	customElements.define('back-in-stock-modal', BackInStockModal);
}
